import React, { useEffect, useState } from 'react';
import { Button, Col, Form, InputNumber, Row, Select, Typography } from 'antd';

const { Text } = Typography;

type UnitKey = 'pack' | 'carton' | 'bag';

const UNITS: Array<{
  key: UnitKey;
  label: string;
  size: string;
  retail: string;
  wholesale: string;
}> = [
  { key: 'pack', label: 'Pack', size: 'pack_size', retail: 'pack_selling_price', wholesale: 'pack_wholesale_price' },
  { key: 'carton', label: 'Carton', size: 'carton_size', retail: 'carton_selling_price', wholesale: 'carton_wholesale_price' },
  { key: 'bag', label: 'Bag', size: 'bag_size', retail: 'bag_selling_price', wholesale: 'bag_wholesale_price' },
];

const moneyFormatter = (value?: string | number) =>
  `Nu. ${value ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const moneyParser = (value?: string) => (value || '').replace(/Nu.\s?|(,*)/g, '') as unknown as number;

function savedSize(value: unknown): boolean {
  const n = Number(value);
  return Number.isFinite(n) && n > 1;
}

/** Choose Pack, Carton, or Bag, then enter only that unit's details. */
export const ProductPackagingFields: React.FC = () => {
  const form = Form.useFormInstance();
  const [open, setOpen] = useState<UnitKey[]>([]);
  const [picker, setPicker] = useState<UnitKey | undefined>(undefined);

  useEffect(() => {
    const values = form.getFieldsValue(UNITS.map((unit) => unit.size));
    const existing = UNITS.filter((unit) => savedSize(values[unit.size])).map((unit) => unit.key);
    setOpen(existing);
  }, [form]);

  const addUnit = (key: UnitKey) => {
    setOpen((prev) => (prev.includes(key) ? prev : [...prev, key]));
  };

  const removeUnit = (unit: (typeof UNITS)[number]) => {
    form.setFieldsValue({
      [unit.size]: undefined,
      [unit.retail]: undefined,
      [unit.wholesale]: undefined,
    });
    setOpen((prev) => prev.filter((key) => key !== unit.key));
  };

  const remaining = UNITS.filter((unit) => !open.includes(unit.key));

  return (
    <div className="product-packaging">
      <div className="product-form-section-title product-form-section-title--spaced">Selling units</div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Stock stays in pieces. Add a pack, carton, or bag only when the till should sell that bundle.
      </Text>
      <Select<UnitKey>
        placeholder="Add a selling unit"
        style={{ width: 280, marginBottom: 12 }}
        value={picker}
        disabled={remaining.length === 0}
        options={remaining.map((unit) => ({ value: unit.key, label: unit.label }))}
        onChange={(value) => {
          if (!value) return;
          addUnit(value);
          setPicker(undefined);
        }}
      />
      {open.length === 0 ? (
        <div className="product-packaging-empty">Sold by the piece only.</div>
      ) : null}
      {UNITS.filter((unit) => open.includes(unit.key)).map((unit) => (
        <div key={unit.key} className="product-packaging-card">
          <div className="product-packaging-card__head">
            <span>{unit.label}</span>
            <Button type="link" danger onClick={() => removeUnit(unit)}>
              Remove
            </Button>
          </div>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name={unit.size}
                label={`Pieces in one ${unit.label.toLowerCase()}`}
                rules={[{ required: true, message: 'Enter at least 2 pieces' }]}
              >
                <InputNumber min={2} precision={0} style={{ width: '100%' }} placeholder="e.g. 10" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={unit.retail} label="Retail price">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Empty = piece price × pieces"
                  formatter={moneyFormatter}
                  parser={moneyParser}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={unit.wholesale} label="Wholesale price">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="Empty = wholesale piece × pieces"
                  formatter={moneyFormatter}
                  parser={moneyParser}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );
};
